import React, { useEffect, useRef, useState } from 'react';
import './PreviewAccessGate.css';

const ACCESS_CODE = '311311';
const COOKIE_NAME = 'realmap_preview_access';
const COOKIE_DAYS = 30;

const setAccessCookie = () => {
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_DAYS);
  document.cookie = `${COOKIE_NAME}=1; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const hasAccessCookie = () => {
  return document.cookie
    .split(';')
    .map((c) => c.trim())
    .some((c) => c.startsWith(`${COOKIE_NAME}=1`));
};

const PreviewAccessGate = ({ children }) => {
  const [unlocked, setUnlocked] = useState(() => hasAccessCookie());
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!unlocked && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [unlocked]);

  const tryVerify = (nextDigits) => {
    const code = nextDigits.join('');
    if (code.length !== 6) return;
    if (code === ACCESS_CODE) {
      setAccessCookie();
      setUnlocked(true);
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => {
        setDigits(['', '', '', '', '', '']);
        setError(false);
        inputsRef.current[0]?.focus();
      }, 700);
    }
  };

  const handleChange = (index, value) => {
    const sanitized = value.replace(/\D/g, '');

    if (sanitized.length > 1) {
      const pasted = sanitized.slice(0, 6).split('');
      const next = ['', '', '', '', '', ''];
      pasted.forEach((d, i) => {
        next[i] = d;
      });
      setDigits(next);
      const lastIndex = Math.min(pasted.length, 6) - 1;
      inputsRef.current[Math.min(lastIndex + 1, 5)]?.focus();
      if (pasted.length === 6) tryVerify(next);
      return;
    }

    const next = [...digits];
    next[index] = sanitized;
    setDigits(next);

    if (sanitized && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }

    if (next.every((d) => d !== '')) {
      tryVerify(next);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
        setDigits(next);
      } else if (index > 0) {
        next[index - 1] = '';
        setDigits(next);
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      tryVerify(digits);
    }
  };

  if (unlocked) return children;

  return (
    <>
      {children}
      <div className="preview-gate-overlay" role="dialog" aria-modal="true">
        <div className={`preview-gate-card ${shake ? 'shake' : ''}`}>
          <div className="preview-gate-badge">معاينة</div>
          <h2 className="preview-gate-title">الموقع في وضع المعاينة</h2>
          <p className="preview-gate-subtitle">
            الرجاء إدخال رمز الدخول المكوّن من 6 أرقام للمتابعة
          </p>

          <div className="preview-gate-inputs" dir="ltr">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`preview-gate-digit ${error ? 'error' : ''}`}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {error && (
            <div className="preview-gate-error">الرمز غير صحيح، حاول مرة أخرى</div>
          )}

          <div className="preview-gate-hint">
            هذه نسخة تجريبية مخصصة للاختبار الداخلي فقط
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewAccessGate;
